import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Gift, Rocket, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './FreeTrialWelcomeModal.scss';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MONTHS_LT = [
  'sausio', 'vasario', 'kovo', 'balandžio', 'gegužės', 'birželio',
  'liepos', 'rugpjūčio', 'rugsėjo', 'spalio', 'lapkričio', 'gruodžio',
];

function formatDateLT(date: Date): string {
  return `${date.getFullYear()} m. ${MONTHS_LT[date.getMonth()]} ${date.getDate()} d.`;
}

function daysUntil(date: Date): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / 86_400_000);
}

// ─── Component ───────────────────────────────────────────────────────────────

const TRIAL_END = new Date('2026-10-01');

interface Props {
  companyName?: string;
  registeredAt: string;  // ISO date string from user.created_at
  onClose: () => void;
}

const FreeTrialWelcomeModal: React.FC<Props> = ({ companyName, registeredAt, onClose }) => {
  const navigate = useNavigate();
  const trialEnd = TRIAL_END;
  const daysLeft = daysUntil(trialEnd);
  const start = new Date(registeredAt);
  const totalDays = Math.round((trialEnd.getTime() - start.getTime()) / 86_400_000);
  const daysUsed = Math.max(0, totalDays - daysLeft);
  const progressPct = totalDays > 0 ? Math.min(100, Math.round((daysUsed / totalDays) * 100)) : 0;

  // Close on Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleStartPosting = () => {
    onClose();
    navigate('/jobs/create');
  };

  const content = (
    <div className="ftw-overlay" onClick={onClose}>
      <div className="ftw-modal" onClick={e => e.stopPropagation()}>

        <button className="ftw-close" onClick={onClose} aria-label="Uždaryti">
          <X size={18} />
        </button>

        <div className="ftw-icon">
          <Gift size={48} strokeWidth={1.5} />
        </div>

        <h2 className="ftw-title">
          Sveikiname{companyName ? `, ${companyName}` : ''}!
        </h2>

        <p className="ftw-lead">
          Kaip naujas <strong>gidas.lt</strong> narys, iki{' '}
          <strong>{formatDateLT(trialEnd)}</strong> galite skelbti
          darbo pasiūlymus <strong>visiškai nemokamai</strong>.
        </p>

        <div className="ftw-days-badge">
          <span className="ftw-days-badge__num">{daysLeft}</span>
          <span className="ftw-days-badge__label">dienos liko</span>
        </div>

        <div className="ftw-progress">
          <div className="ftw-progress__track">
            <div className="ftw-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="ftw-progress__labels">
            <span>Registracija</span>
            <span>{formatDateLT(trialEnd)}</span>
          </div>
        </div>

        <div className="ftw-actions">
          <button className="ftw-btn-primary" onClick={handleStartPosting}>
            <Rocket size={16} />
            Kelti pirmą skelbimą
          </button>
          <button className="ftw-btn-secondary" onClick={onClose}>
            Gerai, supratau
          </button>
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default FreeTrialWelcomeModal;
